import { getInterpretedUA } from '@/utils/browser-detect';

export type UserRole = 'investor' | 'borrower' | 'undefined';

interface ServerUserEntry {
  userId: string
  companysIds: Required<Record<Exclude<UserRole, 'undefined'>, string | null>>
  isAuthenticated: boolean
  isQuickAccessOn: boolean
  isPinNeverInstalled: boolean
  hasPinSet: boolean
  unreadTickets: number
  isReferralParticipant: boolean
}

const lastUserStorageKey = 'last-user' as const;
const lastCompanyStorageKey = 'last-company' as const;
const lastRoleStorageKey = 'last-role' as const;

const storage = localStorage;

function persistToStorage(key: string, payload: string) {
  storage.setItem(key, payload);
}

function retrieveFromStorage(key: string) {
  return storage.getItem(key) as string | null;
}

function removeFromStorage(key: string) {
  return storage.removeItem(key);
}

export const useGlobalAppState = defineStore('app-state', () => {
  const storedLastUserId: string | null = retrieveFromStorage(lastUserStorageKey);
  const userAgentInterpreted = getInterpretedUA();

  const serverData = shallowRef<ServerUserEntry | null>(null);
  const role = ref<UserRole>('undefined');
  const isLoading = ref<boolean>(false);
  const isSessionExpired = ref<boolean>(false);

  const deviceId = ref<string | null>(null);
  const hasDeviceId = computed<boolean>(() => !!deviceId.value?.length);
  const hasPinSet = computed<boolean>(() => !!serverData.value?.hasPinSet);
  const isQuickAccessOn = computed<boolean>(() => !!serverData.value?.isQuickAccessOn);
  const isPinNeverInstalled = computed<boolean>(() => !!serverData.value?.isPinNeverInstalled);

  const userId = computed<string | null>(() => {
    return serverData.value?.userId ?? null;
  });
  const companyId = computed<string | null>(() => {
    if (role.value !== 'undefined' && !!serverData.value) {
      const value = serverData.value.companysIds[role.value];
      value ? persistToStorage(lastCompanyStorageKey, value) : removeFromStorage(lastCompanyStorageKey);
      return value;
    }
    return null;
  });
  const isAuthenticated = computed<boolean>(() =>
    !!serverData.value?.isAuthenticated
    && !!userId.value
    && !isSessionExpired.value
  );

  function updateWithServerEntry(payload: ServerUserEntry) {
    serverData.value = payload;

    if (payload.userId !== storedLastUserId)
      persistToStorage(lastUserStorageKey, payload.userId);
  }

  function pickInitialRole() {
    if (!serverData.value)
      return false;

    const storedRole = retrieveFromStorage(lastRoleStorageKey) as UserRole;
    if (storedLastUserId === serverData.value.userId && storedRole !== 'undefined' && serverData.value.companysIds[storedRole]) {
      changeRoleAndPersist(storedRole);
      return true;
    }

    const { investor, borrower } = serverData.value.companysIds;

    if (investor)
      switchToInvestor();
    else if (borrower)
      switchToBorrower();

    return !!(investor || borrower);
  }

  function switchToBorrower() {
    changeRoleAndPersist('borrower');
  }
  function switchToInvestor() {
    changeRoleAndPersist('investor');
  }
  function switchToUndefinedUser() {
    changeRoleAndPersist('undefined');
  }

  function switchToCompanyById(payload: string) {
    if (!serverData.value || !payload)
      return;

    const roleEntries = Object.entries(serverData.value.companysIds) as [Exclude<UserRole, 'undefined'>, string | null][];
    const estimatedRole = roleEntries.find(([_, cid]) => cid === payload)?.[0] ?? 'undefined';

    changeRoleAndPersist(estimatedRole);
  }

  function startLoader() {
    isLoading.value = true;
  }
  function stopLoader() {
    isLoading.value = false;
  }

  function deactivateSession() {
    isSessionExpired.value = true;
  }
  function activateSession() {
    isSessionExpired.value = false;
  }

  function changeRoleAndPersist(payload: UserRole) {
    if ((!serverData.value && payload !== 'undefined') || !payload)
      return;

    role.value = payload;
    payload !== 'undefined' && persistToStorage(lastRoleStorageKey, payload);
  }

  function setDeviceId(payload: string) {
    if (!payload.length)
      return;

    deviceId.value = payload;
  }

  function retrieveLastCompanyId() {
    return retrieveFromStorage(lastCompanyStorageKey);
  }

  const isReferralAvailable = computed(() => {
    if (import.meta.env.VITE_APP_IS_REFERRAL_AVAILABLE === 'FALSE')
      return false;

    return serverData.value?.isReferralParticipant;
  });

  function reset() {
    serverData.value = null;
    switchToUndefinedUser();
    isSessionExpired.value = false;
  }

  async function resetAfter(callback: CallableFunction) {
    startLoader();
    await callback();
    reset();
    stopLoader();
  }

  return {
    role: readonly(role),
    userId,
    companyId,
    deviceId: readonly(deviceId),
    userAgentInterpreted,

    hasPinSet,
    isQuickAccessOn,
    hasDeviceId,
    isPinNeverInstalled,
    isLoading: readonly(isLoading),
    isAuthenticated,
    isSessionExpired: readonly(isSessionExpired),

    updateWithServerEntry,

    switchToBorrower,
    switchToInvestor,
    switchToUndefinedUser,
    switchToCompanyById,
    pickInitialRole,

    startLoader,
    stopLoader,

    deactivateSession,
    activateSession,
    setDeviceId,

    retrieveLastCompanyId,

    reset,
    resetAfter,

    isReferralAvailable,
  };
});
